import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Avatar,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Alert,
  Switch,
  FormControlLabel,
  CircularProgress,
  Tooltip,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Slider,
  Menu,
  Popper,
  ClickAwayListener,
  InputAdornment,
} from '@mui/material';
import {
  Send as SendIcon,
  WhatsApp as WhatsAppIcon,
  Image as ImageIcon,
  AttachFile as AttachFileIcon,
  SmartToy as AIIcon,
  VolumeUp as VoiceIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Mic as MicIcon,
  Stop as StopIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Refresh as RefreshIcon,
  Assessment as ReportsIcon,
  Videocam as VideoIcon,
  InsertDriveFile as DocumentIcon,
  Download as DownloadIcon,
  LocationOn as LocationOnIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import WhatsAppService from '../../services/whatsapp.service';
import ContactService from '../../services/contact.service';
import { requestService } from '../../services/requestService';
import api from '../../api/axios';
import { QUICK_MESSAGES, QuickMessage, findMatchingMessages } from '../../constants/whatsappQuickMessages';
import CustomerQuickSearch from '../../components/common/CustomerQuickSearch';
import { normalizePhoneDigits } from '../../utils/customerSearch';

const API_URL = (process.env.REACT_APP_API_URL || 'https://darb.aiastra.io').replace(/\/$/, '');

// Resolve message image: backend Strapi path, full URL, or WhatsApp media ID (use proxy)
function getMessageImageDisplay(mediaUrl: string | null | undefined): { url: string | null; mediaId: string | null } {
  if (!mediaUrl || typeof mediaUrl !== 'string') return { url: null, mediaId: null };
  const trimmed = mediaUrl.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return { url: trimmed, mediaId: null };
  if (trimmed.startsWith('/')) return { url: `${API_URL}${trimmed}`, mediaId: null };
  // Numeric or string ID = WhatsApp media ID, must be loaded via authenticated proxy
  return { url: null, mediaId: trimmed };
}

// Load WhatsApp media via backend proxy (auth required) and show as image
const WhatsAppMediaImage: React.FC<{
  mediaId: string;
  alt?: string;
  sx?: object;
  onClick?: () => void;
}> = ({ mediaId, alt = 'WhatsApp Image', sx = {}, onClick }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  useEffect(() => {
    let objectUrl: string | null = null;
    setLoading(true);
    setError(false);
    api
      .get(`/whatsapp-messages/media/${encodeURIComponent(mediaId)}`, { responseType: 'arraybuffer' })
      .then((res) => {
        const contentType = res.headers['content-type'] || 'image/jpeg';
        const blob = new Blob([res.data], { type: contentType });
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
        setError(false);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaId]);
  if (error || (!loading && !src)) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', p: 1, bgcolor: '#f0f2f5', borderRadius: '8px', ...sx }}>
        <ImageIcon sx={{ fontSize: 20, mr: 1, color: '#667781' }} />
        <Typography variant="caption" sx={{ color: '#667781', fontSize: '0.75rem' }}>
          Image (unavailable)
        </Typography>
      </Box>
    );
  }
  if (loading || !src) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 80, bgcolor: '#f0f2f5', borderRadius: '8px', ...sx }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{
        maxWidth: '100%',
        maxHeight: '300px',
        borderRadius: '8px',
        cursor: 'pointer',
        '&:hover': { opacity: 0.9 },
        ...sx,
      }}
      onClick={onClick}
    />
  );
};

// Hook: fetch any WhatsApp media via proxy and return blob URL
function useWhatsAppMedia(mediaId: string | null) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!mediaId);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!mediaId) {
      setLoading(false);
      setBlobUrl(null);
      return;
    }
    let objectUrl: string | null = null;
    setLoading(true);
    setError(false);
    api
      .get(`/whatsapp-messages/media/${encodeURIComponent(mediaId)}`, { responseType: 'arraybuffer' })
      .then((res) => {
        const contentType = res.headers['content-type'] || 'application/octet-stream';
        const blob = new Blob([res.data], { type: contentType });
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
        setError(false);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaId]);
  return { blobUrl, loading, error };
}

// Voice message bubble – WhatsApp style: play/pause, progress, duration
const WhatsAppVoiceMessage: React.FC<{
  mediaId: string;
  durationSeconds?: number;
  isOutbound?: boolean;
}> = ({ mediaId, durationSeconds, isOutbound }) => {
  const { blobUrl, loading, error } = useWhatsAppMedia(mediaId);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationSeconds ?? 0);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTimeUpdate = () => setCurrentTime(el.currentTime);
    const onDurationChange = () => setDuration(el.duration);
    const onEnded = () => {
      setPlaying(false);
      setCurrentTime(0);
    };
    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('durationchange', onDurationChange);
    el.addEventListener('ended', onEnded);
    return () => {
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('durationchange', onDurationChange);
      el.removeEventListener('ended', onEnded);
    };
  }, [blobUrl]);

  useEffect(() => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.play();
    else audioRef.current.pause();
  }, [playing, blobUrl]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec < 10 ? '0' : ''}${sec}`;
  };

  if (error || (!loading && !blobUrl)) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, bgcolor: '#f0f2f5', borderRadius: '8px', minWidth: 200 }}>
        <VoiceIcon sx={{ fontSize: 28, color: '#667781', mr: 1 }} />
        <Typography variant="caption" sx={{ color: '#667781' }}>Voice message (unavailable)</Typography>
      </Box>
    );
  }
  if (loading || !blobUrl) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, minWidth: 200, bgcolor: '#f0f2f5', borderRadius: '8px' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 220, maxWidth: 280 }}>
      <audio ref={audioRef} src={blobUrl} />
      <IconButton
        size="small"
        onClick={() => setPlaying((p) => !p)}
        sx={{
          bgcolor: isOutbound ? 'rgba(255,255,255,0.9)' : '#25d366',
          color: isOutbound ? '#111b21' : '#fff',
          '&:hover': { bgcolor: isOutbound ? '#fff' : '#20bd5a' },
        }}
      >
        {playing ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
      </IconButton>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Slider
          size="small"
          value={progress}
          sx={{
            color: isOutbound ? '#8696a0' : 'rgba(255,255,255,0.8)',
            height: 4,
            '& .MuiSlider-thumb': { width: 12, height: 12 },
          }}
          onChange={(_, value) => {
            const t = (Number(value) / 100) * duration;
            if (audioRef.current) {
              audioRef.current.currentTime = t;
              setCurrentTime(t);
            }
          }}
        />
        <Typography variant="caption" sx={{ color: isOutbound ? '#667781' : 'rgba(255,255,255,0.9)', fontSize: '0.7rem' }}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </Typography>
      </Box>
    </Box>
  );
};

// Video message – inline player with controls
const WhatsAppVideoMessage: React.FC<{ mediaId: string }> = ({ mediaId }) => {
  const { blobUrl, loading, error } = useWhatsAppMedia(mediaId);
  if (error || (!loading && !blobUrl)) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, bgcolor: '#f0f2f5', borderRadius: '8px' }}>
        <VideoIcon sx={{ fontSize: 24, mr: 1, color: '#667781' }} />
        <Typography variant="caption" sx={{ color: '#667781' }}>Video (unavailable)</Typography>
      </Box>
    );
  }
  if (loading || !blobUrl) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120, bgcolor: '#f0f2f5', borderRadius: '8px' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  return (
    <Box
      component="video"
      src={blobUrl}
      controls
      playsInline
      sx={{
        maxWidth: '100%',
        maxHeight: 320,
        borderRadius: '8px',
        backgroundColor: '#000',
      }}
    />
  );
};

// Document message – icon, filename, download
const WhatsAppDocumentMessage: React.FC<{
  mediaId: string;
  filename: string;
}> = ({ mediaId, filename }) => {
  const { blobUrl, loading, error } = useWhatsAppMedia(mediaId);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = () => {
    if (!blobUrl) return;
    setDownloading(true);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'document';
    a.click();
    setDownloading(false);
  };

  if (error || (!loading && !blobUrl)) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, bgcolor: '#f0f2f5', borderRadius: '8px' }}>
        <DocumentIcon sx={{ fontSize: 28, mr: 1, color: '#667781' }} />
        <Typography variant="caption" sx={{ color: '#667781' }}>Document (unavailable)</Typography>
      </Box>
    );
  }
  if (loading || !blobUrl) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, minWidth: 200, bgcolor: '#f0f2f5', borderRadius: '8px' }}>
        <CircularProgress size={22} />
        <Typography variant="caption" sx={{ ml: 1, color: '#667781' }}>Loading…</Typography>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        bgcolor: 'rgba(0,0,0,0.05)',
        borderRadius: '8px',
        border: '1px solid rgba(0,0,0,0.08)',
        maxWidth: 280,
      }}
    >
      <DocumentIcon sx={{ fontSize: 36, color: '#667781' }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {filename || 'Document'}
        </Typography>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={downloading}
          sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.75rem' }}
        >
          Download
        </Button>
      </Box>
    </Box>
  );
};

const WhatsAppChat: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const phoneParam = searchParams.get('phone');
  const [contacts, setContacts] = useState<any[]>([]);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [messageType, setMessageType] = useState('text');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [serviceNumberSearch, setServiceNumberSearch] = useState('');
  const [mobileNumberSearch, setMobileNumberSearch] = useState('');
  const [requestSearchMatches, setRequestSearchMatches] = useState<any[]>([]);
  
  // Quick Messages State
  const [showQuickMessages, setShowQuickMessages] = useState(false);
  const [autocompleteAnchor, setAutocompleteAnchor] = useState<HTMLElement | null>(null);
  const [autocompleteQuery, setAutocompleteQuery] = useState('');
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState<QuickMessage[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Voice Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Selected contact (support both Strapi format and plain object from WhatsApp contacts endpoint)
  const selectedContactData = selectedContact?.attributes || selectedContact;

  const normalizePhone = (phone: string | undefined | null) =>
    (phone || '').replace(/\D/g, '').slice(-8);

  useEffect(() => {
    const serviceQuery = serviceNumberSearch.trim();
    const mobileQuery = mobileNumberSearch.trim();
    if (serviceQuery.length < 2 && mobileQuery.length < 3) {
      setRequestSearchMatches([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const digits = normalizePhoneDigits(mobileQuery);
        const params: Record<string, unknown> = {
          'pagination[limit]': 15,
          sort: 'createdAt:desc',
        };
        if (serviceQuery.length >= 2 && digits.length >= 3) {
          params['filters[$or][0][requestNumber][$containsi]'] = serviceQuery;
          params['filters[$or][1][phoneNumber][$contains]'] = digits;
        } else if (serviceQuery.length >= 2) {
          params['filters[requestNumber][$containsi]'] = serviceQuery;
        } else if (digits.length >= 3) {
          params['filters[phoneNumber][$contains]'] = digits;
        }
        const response = await requestService.getAll(params);
        setRequestSearchMatches(response.data || []);
      } catch {
        setRequestSearchMatches([]);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [serviceNumberSearch, mobileNumberSearch]);

  const filteredContacts = useMemo(() => {
    const serviceQuery = serviceNumberSearch.trim().toLowerCase();
    const mobileDigits = normalizePhoneDigits(mobileNumberSearch);

    if (!serviceQuery && !mobileDigits) return contacts;

    const matchedPhones = new Set(
      requestSearchMatches.map((req) => {
        const attrs = req.attributes || req;
        return normalizePhone(attrs.phoneNumber);
      }).filter(Boolean)
    );

    return contacts.filter((contact) => {
      const contactData = contact.attributes || contact;
      const phone = (contactData?.whatsappNumber || contactData?.phone || contact.whatsappNumber || contact.phone || '');
      const phoneDigits = phone.replace(/\D/g, '');

      if (mobileDigits && phoneDigits.includes(mobileDigits)) return true;
      if (serviceQuery && matchedPhones.size > 0 && matchedPhones.has(normalizePhone(phone))) return true;
      return false;
    });
  }, [contacts, serviceNumberSearch, mobileNumberSearch, requestSearchMatches]);

  const phonesMatch = (a: string | undefined | null, b: string | undefined | null) => {
    const da = normalizePhoneDigits(a);
    const db = normalizePhoneDigits(b);
    if (!da || !db) return false;
    if (da === db) return true;
    if (da.length >= 8 && db.length >= 8 && da.slice(-8) === db.slice(-8)) return true;
    return da.endsWith(db) || db.endsWith(da);
  };

  const loadContactByPhone = async (phone: string) => {
    const targetDigits = normalizePhoneDigits(phone);
    if (!targetDigits) return;

    const existing = contacts.find((contact) => {
      const contactData = contact.attributes || contact;
      const contactPhone =
        contactData?.whatsappNumber ||
        contactData?.phone ||
        contact.whatsappNumber ||
        contact.phone;
      return phonesMatch(contactPhone, targetDigits);
    });

    if (existing) {
      setSelectedContact(existing);
      return;
    }

    try {
      const response = await ContactService.getAll({
        'filters[$or][0][whatsappNumber][$contains]': targetDigits.slice(-8),
        'filters[$or][1][phone][$contains]': targetDigits.slice(-8),
        populate: '*',
        'pagination[limit]': 5,
      });
      const found = (response.data?.data || []).find((contact: any) => {
        const contactData = contact.attributes || contact;
        const contactPhone = contactData?.whatsappNumber || contactData?.phone;
        return phonesMatch(contactPhone, targetDigits);
      });
      if (found) {
        setSelectedContact(found);
        setContacts((prev) => {
          const foundId = found.id || found.attributes?.id;
          if (prev.some((c) => (c.id || c.attributes?.id) === foundId)) return prev;
          return [found, ...prev];
        });
      }
    } catch (error) {
      console.error('Error loading contact by phone:', error);
    }
  };

  useEffect(() => {
    fetchContacts();
    
    // Refresh contacts every 10 seconds to catch new ones
    const contactsInterval = setInterval(() => {
      fetchContacts();
    }, 10000);
    
    return () => clearInterval(contactsInterval);
  }, []);

  useEffect(() => {
    if (id) {
      loadContact(parseInt(id, 10));
    } else if (phoneParam) {
      loadContactByPhone(phoneParam);
    }
  }, [id, phoneParam, contacts.length]);

  useEffect(() => {
    if (selectedContact) {
      console.log('🔵 [CONTACT] Selected contact changed:', {
        id: selectedContact.id,
        name: `${selectedContact.attributes?.firstName || selectedContact.firstName} ${selectedContact.attributes?.lastName || selectedContact.lastName}`,
        timestamp: new Date().toISOString()
      });
      fetchMessages();
      // Poll for new messages every 5 seconds
      const interval = setInterval(() => {
        fetchMessages();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedContact]);

  const fetchContacts = async () => {
    try {
      // Use the custom endpoint to get contacts that have WhatsApp messages
      const response = await WhatsAppService.getWhatsAppContacts();
      // Contacts are already sorted by backend (newest first)
      const contactsList = response.data?.data || [];
      setContacts(contactsList);
      console.log('📋 [CONTACTS] Loaded', contactsList.length, 'contacts');
      
      // Don't update selectedContact here to avoid retriggering useEffect
      // The contact data in the list is sufficient for display
    } catch (error) {
      console.error('❌ [CONTACTS] Error fetching contacts:', error);
      // Fallback to old method if new endpoint fails
      try {
        const fallbackResponse = await ContactService.getAll({
          'filters[whatsappNumber][$notNull]': true,
          populate: '*',
        });
        setContacts(fallbackResponse.data?.data || []);
      } catch (fallbackError) {
        console.error('❌ [CONTACTS] Error with fallback contact fetch:', fallbackError);
      }
    }
  };

  const loadContact = async (contactId: number) => {
    try {
      // First try to find in current contacts list
      const existingContact = contacts.find(c => c.id === contactId);
      if (existingContact) {
        setSelectedContact(existingContact);
        return;
      }
      
      // If not found, fetch from API
      const response = await ContactService.getOne(contactId);
      const contact = response.data?.data;
      setSelectedContact(contact);
    } catch (error) {
      console.error('Error loading contact:', error);
    }
  };


  const fetchMessages = async () => {
    if (!selectedContact) return;
    
    try {
      const contactId = selectedContact.id || selectedContact.attributes?.id;
      if (!contactId) {
        console.error('❌ [MESSAGES] No contact ID found');
        return;
      }
      
      console.log('📨 [MESSAGES] Fetching messages for contact:', {
        id: contactId,
        name: `${selectedContact.attributes?.firstName || selectedContact.firstName} ${selectedContact.attributes?.lastName || selectedContact.lastName}`
      });
      
      const response = await WhatsAppService.getContactMessages(contactId);
      // Sort messages by createdAt (oldest first for chat display)
      const sortedMessages = (response.data?.data || []).sort((a: any, b: any) => {
        const dateA = new Date(a.attributes?.createdAt || a.createdAt || 0).getTime();
        const dateB = new Date(b.attributes?.createdAt || b.createdAt || 0).getTime();
        return dateA - dateB; // Oldest first
      });
      
      console.log('✅ [MESSAGES] Loaded', sortedMessages.length, 'messages for contact:', contactId);
      
      setMessages(sortedMessages);
      
      // Refresh contacts to update unread count
      fetchContacts();
    } catch (error) {
      console.error('❌ [MESSAGES] Error fetching messages:', error);
    }
  };
  
  // Format time for display (like WhatsApp) - for contact list
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  // Format date header for messages (Today, Yesterday, or date)
  const formatDateHeader = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (messageDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (messageDate.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    } else {
      // Check if it's within the last week
      const diffDays = Math.floor((today.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays < 7) {
        return date.toLocaleDateString('en-US', { weekday: 'long' });
      } else {
        return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
      }
    }
  };
  
  // Format message time with date if needed
  const formatMessageTime = (dateString: string, showDate: boolean = false) => {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const isToday = messageDate.getTime() === today.getTime();
    
    const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    
    if (showDate && !isToday) {
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `${dateStr}, ${timeStr}`;
    }
    
    return timeStr;
  };
  
  // Check if we need to show a date header before this message
  const shouldShowDateHeader = (currentMsg: any, previousMsg: any | null) => {
    if (!previousMsg) return true;
    
    const currentDate = new Date(currentMsg.attributes?.createdAt || currentMsg.attributes?.sentAt || currentMsg.createdAt);
    const previousDate = new Date(previousMsg.attributes?.createdAt || previousMsg.attributes?.sentAt || previousMsg.createdAt);
    
    const currentDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    const previousDay = new Date(previousDate.getFullYear(), previousDate.getMonth(), previousDate.getDate());
    
    return currentDay.getTime() !== previousDay.getTime();
  };
  
  // Format last message preview
  const formatMessagePreview = (message: string, messageType: string) => {
    if (!message) return '';
    if (messageType === 'image') return '📷 Image';
    if (messageType === 'audio') return '🎤 Audio';
    if (messageType === 'video') return '🎥 Video';
    if (messageType === 'document') return '📄 Document';
    if (messageType === 'location') return '📍 Location';
    if (messageType === 'template') return '📋 Template';
    // Truncate long messages
    return message.length > 50 ? message.substring(0, 50) + '...' : message;
  };


  const handleSendMessage = async (messageText?: string) => {
    const messageToSend = messageText || newMessage;
    if (!messageToSend.trim() || !selectedContact) return;

    setError('');
    setSuccess('');
    setSending(true);
    setAutocompleteAnchor(null);
    setAutocompleteQuery('');
    setAutocompleteSuggestions([]);

    try {
      const contactId = selectedContact.id || selectedContact.attributes?.id;
      const phoneNumber = selectedContact.attributes?.whatsappNumber || 
                         selectedContact.whatsappNumber ||
                         selectedContact.attributes?.phone ||
                         selectedContact.phone;
      
      if (!phoneNumber) {
        setError('Phone number not found for this contact');
        setSending(false);
        return;
      }
      
      // Send regular message via WhatsApp API
      await WhatsAppService.sendMessage({
        contact: contactId,
        phoneNumber,
        message: messageToSend,
        messageType: messageType as any,
        direction: 'outbound',
      });
      
      setSuccess('Message sent successfully!');
      setNewMessage('');
      
      // Refresh contacts first to update sorting (newest message first)
      await fetchContacts();
      
      // Refresh messages to show the sent message
      setTimeout(() => {
        fetchMessages();
      }, 500);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Handle quick message button click
  const handleQuickMessageClick = async (quickMessage: QuickMessage) => {
    await handleSendMessage(quickMessage.message);
  };

  // Handle "/" command autocomplete
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewMessage(value);

    // Check if user is typing "/" command
    if (value.startsWith('/')) {
      const query = value.substring(1).trim();
      setAutocompleteQuery(query);
      
      if (query === '') {
        // Show all quick messages
        setAutocompleteSuggestions(QUICK_MESSAGES);
      } else {
        // Filter messages based on query
        const matches = findMatchingMessages(query);
        setAutocompleteSuggestions(matches);
      }
      
      // Set anchor for autocomplete dropdown
      if (inputRef.current) {
        setAutocompleteAnchor(inputRef.current);
      }
      setSelectedSuggestionIndex(0);
    } else {
      // Close autocomplete if not starting with "/"
      setAutocompleteAnchor(null);
      setAutocompleteQuery('');
      setAutocompleteSuggestions([]);
    }
  };

  // Handle keyboard navigation in autocomplete
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (autocompleteAnchor && autocompleteSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < autocompleteSuggestions.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : 0);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const selectedMessage = autocompleteSuggestions[selectedSuggestionIndex];
        if (selectedMessage) {
          setNewMessage(selectedMessage.message);
          setAutocompleteAnchor(null);
          setAutocompleteQuery('');
          setAutocompleteSuggestions([]);
          // Optionally auto-send, or just insert into input
          // handleSendMessage(selectedMessage.message);
        }
      } else if (e.key === 'Escape') {
        setAutocompleteAnchor(null);
        setAutocompleteQuery('');
        setAutocompleteSuggestions([]);
      }
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Voice Recording Functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Failed to access microphone. Please grant permission.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  };


  const playAudio = (audioBase64: string, messageId: string) => {
    try {
      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      // Decode base64 and create audio element
      const audioData = atob(audioBase64);
      const audioArray = new Uint8Array(audioData.length);
      for (let i = 0; i < audioData.length; i++) {
        audioArray[i] = audioData.charCodeAt(i);
      }
      const audioBlob = new Blob([audioArray], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);

      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      setPlayingAudio(messageId);

      audio.onended = () => {
        setPlayingAudio(null);
        URL.revokeObjectURL(audioUrl);
      };

      audio.play();
    } catch (error) {
      console.error('Error playing audio:', error);
      setError('Failed to play audio');
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlayingAudio(null);
    }
  };


  // Auto-clear success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#e5ddd5' }}>
      <Stack direction="row" spacing={2} alignItems="center" mb={2} flexWrap="wrap" sx={{ p: 2, bgcolor: '#075e54', color: 'white' }}>
        <WhatsAppIcon fontSize="large" />
        <Typography variant="h5" sx={{ fontWeight: 500 }}>WhatsApp</Typography>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={0} sx={{ flex: 1, overflow: 'hidden' }}>
        {/* Contacts List - WhatsApp Style */}
        <Box sx={{ 
          width: { xs: '100%', md: '350px' }, 
          bgcolor: '#ffffff',
          borderRight: '1px solid #e0e0e0',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <Box sx={{ p: 1.5, bgcolor: '#f0f2f5', borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#41525d', mb: 1 }}>
              Chats
            </Typography>
            <CustomerQuickSearch
              title=""
              embedded
              serviceNumber={serviceNumberSearch}
              mobileNumber={mobileNumberSearch}
              onServiceNumberChange={setServiceNumberSearch}
              onMobileNumberChange={setMobileNumberSearch}
              onClear={() => {
                setServiceNumberSearch('');
                setMobileNumberSearch('');
              }}
            />
            {(serviceNumberSearch.trim() || mobileNumberSearch.trim()) && requestSearchMatches.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {requestSearchMatches.slice(0, 5).map((req) => {
                  const attrs = req.attributes || req;
                  const reqPhone = normalizePhone(attrs.phoneNumber);
                  const matchingContact = contacts.find((c) => {
                    const d = c.attributes || c;
                    return normalizePhone(d.whatsappNumber || d.phone) === reqPhone;
                  });
                  return (
                    <Box
                      key={req.id}
                      onClick={() => matchingContact && setSelectedContact(matchingContact)}
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        cursor: matchingContact ? 'pointer' : 'default',
                        bgcolor: matchingContact ? '#e7f8ef' : '#fff3e0',
                        mb: 0.5,
                        '&:hover': matchingContact ? { bgcolor: '#d9f5e8' } : undefined,
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                        {attrs.requestNumber || `REQ-${req.id}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {attrs.phoneNumber || 'No phone'}
                        {!matchingContact && ' — no chat yet'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {filteredContacts.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {serviceNumberSearch.trim() || mobileNumberSearch.trim() ? 'No matching conversations' : 'No WhatsApp contacts yet'}
                </Typography>
                {!serviceNumberSearch.trim() && !mobileNumberSearch.trim() && (
                  <Typography variant="caption" color="text.secondary">
                    Contacts will appear here when messages are sent or received
                  </Typography>
                )}
              </Box>
            ) : (
              filteredContacts.map((contact) => {
                const contactData = contact.attributes || contact;
                const unreadCount = contact.unreadCount || 0;
                const latestMessage = contact.latestMessage || '';
                const latestMessageType = contact.latestMessageType || 'text';
                const latestMessageDate = contact.latestMessageDate || contactData?.latestMessageDate;
                const isSelected = selectedContact?.id === contact.id;
                const phoneNumber =
                  contactData?.whatsappNumber ||
                  contactData?.phone ||
                  contact.whatsappNumber ||
                  contact.phone;
                const chatRole = contact.chatRole as 'service_provider' | 'client' | null | undefined;
                const clientName = contact.clientName as string | null | undefined;
                const companyName = contact.companyName as string | null | undefined;
                const providerName = contact.serviceProviderName as string | null | undefined;
                const displayName =
                  chatRole === 'client' && clientName
                    ? clientName
                    : chatRole === 'service_provider' && providerName
                      ? providerName
                      : `${contactData?.firstName || 'WhatsApp'} ${contactData?.lastName || 'User'}`.trim();
                
                return (
                  <Box
                    key={contact.id}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent event bubbling
                      console.log('👆 [CLICK] Contact clicked:', {
                        clickedId: contact.id,
                        clickedName: `${contactData?.firstName || 'WhatsApp'} ${contactData?.lastName || 'User'}`,
                        currentSelectedId: selectedContact?.id,
                        currentSelectedName: selectedContact ? `${selectedContact.attributes?.firstName || selectedContact.firstName} ${selectedContact.attributes?.lastName || selectedContact.lastName}` : 'none',
                        timestamp: new Date().toISOString()
                      });
                      
                      // Only update if clicking a different contact
                      if (selectedContact?.id !== contact.id) {
                        console.log('✨ [CLICK] Switching to new contact:', contact.id);
                        setSelectedContact(contact);
                      } else {
                        console.log('ℹ️ [CLICK] Same contact clicked, ignoring');
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: isSelected ? '#e9edef' : 'transparent',
                      borderLeft: isSelected ? '3px solid #25d366' : '3px solid transparent',
                      '&:hover': { bgcolor: '#f5f6f6' },
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <Box sx={{ display: 'flex', p: 1.5, alignItems: 'center' }}>
                      <Badge
                        variant="dot"
                        invisible={unreadCount === 0}
                        color="error"
                        sx={{
                          '& .MuiBadge-badge': {
                            bgcolor: '#25d366',
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            border: '2px solid white',
                          }
                        }}
                      >
                        <Avatar 
                          sx={{ 
                            width: 50, 
                            height: 50, 
                            bgcolor: '#25d366',
                            fontWeight: 500,
                            fontSize: '1.2rem'
                          }}
                        >
                          {(contactData?.firstName?.[0] || 'W').toUpperCase()}
                          {(contactData?.lastName?.[0] || '').toUpperCase()}
                        </Avatar>
                      </Badge>
                      <Box sx={{ flex: 1, ml: 2, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
                            <Typography 
                              variant="subtitle2" 
                              sx={{ 
                                fontWeight: unreadCount > 0 ? 600 : 400,
                                color: '#111b21',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {displayName}
                            </Typography>
                            {chatRole === 'service_provider' && (
                              <Chip
                                label={t('whatsappChat.serviceProvider')}
                                size="small"
                                color="warning"
                                sx={{ height: 20, fontSize: '0.65rem', flexShrink: 0 }}
                              />
                            )}
                            {chatRole === 'client' && companyName && (
                              <Chip
                                label={companyName}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.65rem', flexShrink: 0, maxWidth: 120 }}
                              />
                            )}
                          </Box>
                          {latestMessageDate && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#667781',
                                fontSize: '0.75rem',
                                ml: 1,
                                flexShrink: 0
                              }}
                            >
                              {formatTime(latestMessageDate)}
                            </Typography>
                          )}
                        </Box>
                        {phoneNumber && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#667781',
                              fontSize: '0.75rem',
                              fontFamily: 'monospace',
                              display: 'block',
                              mb: 0.25,
                            }}
                          >
                            {phoneNumber}
                          </Typography>
                        )}
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#667781',
                            fontSize: '0.875rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: unreadCount > 0 ? 500 : 400
                          }}
                        >
                          {formatMessagePreview(latestMessage, latestMessageType)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>

        {/* Chat Area - WhatsApp Style */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#efeae2', backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'grid\' width=\'100\' height=\'100\' patternUnits=\'userSpaceOnUse\'%3E%3Cpath d=\'M 100 0 L 0 0 0 100\' fill=\'none\' stroke=\'%23e0e0e0\' stroke-width=\'0.5\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'100\' height=\'100\' fill=\'url(%23grid)\' opacity=\'0.1\'/%3E%3C/svg%3E")' }}>
          {selectedContact ? (
            <>
              {/* Chat Header - WhatsApp Style */}
              <Box sx={{ 
                p: 1.5, 
                bgcolor: '#075e54', 
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Avatar 
                      sx={{ 
                        width: 40, 
                        height: 40, 
                        bgcolor: '#25d366',
                        mr: 1.5
                      }}
                    >
                    {(
                      (selectedContactData?.firstName?.[0] || 'W') + 
                      (selectedContactData?.lastName?.[0] || '')
                    ).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      {selectedContactData?.firstName}{' '}
                      {selectedContactData?.lastName}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.75rem' }}>
                      {selectedContactData?.whatsappNumber || selectedContactData?.phone}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Start Flow Button */}
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<WhatsAppIcon />}
                    onClick={async () => {
                      if (!selectedContact) return;
                      
                      try {
                        setSending(true);
                        setError('');
                        setSuccess('');
                        
                        const phoneNumber = selectedContact.attributes?.whatsappNumber || 
                                           selectedContact.attributes?.phone || 
                                           selectedContact.whatsappNumber || 
                                           selectedContact.phone;
                        
                        if (!phoneNumber) {
                          setError('Phone number not found for this contact');
                          return;
                        }
                        
                        // Call start client flow API
                        await WhatsAppService.startClientFlow(phoneNumber);
                        
                        setSuccess('Client flow started successfully! The language selection template has been sent.');
                        
                        // Refresh messages to show the sent template
                        setTimeout(() => {
                          fetchMessages();
                        }, 1000);
                      } catch (err: any) {
                        console.error('Error starting client flow:', err);
                        setError(err.response?.data?.error?.message || err.message || 'Failed to start client flow');
                      } finally {
                        setSending(false);
                      }
                    }}
                    disabled={sending}
                    sx={{ 
                      whiteSpace: 'nowrap',
                      bgcolor: '#25d366',
                      '&:hover': { bgcolor: '#128c7e' },
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      px: 1.5
                    }}
                  >
                    {sending ? 'Starting...' : 'Start Flow'}
                  </Button>
                </Box>

                {/* Quick Message Buttons */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setShowQuickMessages(!showQuickMessages)}
                    endIcon={showQuickMessages ? <KeyboardArrowDownIcon /> : <KeyboardArrowDownIcon sx={{ transform: 'rotate(180deg)' }} />}
                    sx={{
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.3)',
                      '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.1)' },
                      textTransform: 'none',
                      fontSize: '0.7rem',
                      py: 0.5,
                      px: 1
                    }}
                  >
                    Quick Messages
                  </Button>
                  {showQuickMessages && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', width: '100%' }}>
                      {QUICK_MESSAGES.map((msg) => (
                        <Button
                          key={msg.id}
                          size="small"
                          variant="outlined"
                          onClick={() => handleQuickMessageClick(msg)}
                          disabled={sending}
                          sx={{
                            color: 'white',
                            borderColor: 'rgba(255,255,255,0.3)',
                            '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.1)' },
                            textTransform: 'none',
                            fontSize: '0.65rem',
                            py: 0.25,
                            px: 0.75,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {msg.label}
                        </Button>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Messages - WhatsApp Style */}
              <Box sx={{ 
                flex: 1, 
                p: 2, 
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {messages.length === 0 ? (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100%',
                    flexDirection: 'column'
                  }}>
                    <WhatsAppIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                    <Typography color="text.secondary" variant="h6">
                      No messages yet
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      Start the conversation!
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={1.5} sx={{ pb: 2 }}>
                    {messages.map((msg, index) => {
                      const isOutbound = msg.attributes?.direction === 'outbound';
                      const isAIGenerated = msg.attributes?.metadata?.aiGenerated;
                      const hasVoiceResponse = msg.attributes?.metadata?.aiResponse?.audioResponse?.audio_base64;
                      const msgTime = msg.attributes?.createdAt || msg.attributes?.sentAt || msg.createdAt;
                      const status = msg.attributes?.status || 'delivered';
                      const previousMsg = index > 0 ? messages[index - 1] : null;
                      const showDateHeader = shouldShowDateHeader(msg, previousMsg);
                      
                      return (
                        <React.Fragment key={msg.id}>
                          {showDateHeader && (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                my: 1.5
                              }}
                            >
                              <Box
                                sx={{
                                  bgcolor: 'rgba(225, 245, 254, 0.92)',
                                  px: 2,
                                  py: 0.5,
                                  borderRadius: '7.5px',
                                  boxShadow: '0 1px 1px rgba(0,0,0,0.1)'
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#54656f',
                                    fontSize: '0.75rem',
                                    fontWeight: 500
                                  }}
                                >
                                  {formatDateHeader(msgTime)}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: isOutbound ? 'flex-end' : 'flex-start',
                              width: '100%'
                            }}
                          >
                          <Box
                            sx={{
                              maxWidth: '65%',
                              minWidth: '120px',
                              p: 1.5,
                              borderRadius: '7.5px',
                              bgcolor: isOutbound ? '#dcf8c6' : '#ffffff',
                              boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
                              border: isAIGenerated ? '2px solid #9c27b0' : 'none',
                              position: 'relative',
                              '&::before': isOutbound ? {
                                content: '""',
                                position: 'absolute',
                                right: '-8px',
                                top: '0',
                                width: 0,
                                height: 0,
                                borderTop: '8px solid transparent',
                                borderBottom: '8px solid transparent',
                                borderLeft: `8px solid #dcf8c6`,
                              } : {
                                content: '""',
                                position: 'absolute',
                                left: '-8px',
                                top: '0',
                                width: 0,
                                height: 0,
                                borderTop: '8px solid transparent',
                                borderBottom: '8px solid transparent',
                                borderRight: `8px solid #ffffff`,
                              }
                            }}
                          >
                            {isAIGenerated && (
                              <Chip
                                icon={<AIIcon />}
                                label="AI Response"
                                size="small"
                                sx={{ 
                                  mb: 1,
                                  bgcolor: '#9c27b0',
                                  color: 'white',
                                  height: '20px',
                                  fontSize: '0.7rem',
                                  '& .MuiChip-icon': { color: 'white' }
                                }}
                              />
                            )}
                            
                            {/* Message text – hide for audio/document/location (shown in media UI); show caption for video below player */}
                            {!(msg.attributes?.messageType === 'audio' || msg.attributes?.messageType === 'document' || msg.attributes?.messageType === 'location') && (
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  whiteSpace: 'pre-wrap', 
                                  wordBreak: 'break-word',
                                  fontSize: '0.95rem',
                                  color: '#111b21',
                                  mb: 0.5
                                }}
                              >
                                {msg.attributes?.message || msg.message}
                              </Typography>
                            )}
                            
                            {/* Show template name if it's a template message */}
                            {msg.attributes?.messageType === 'template' && msg.attributes?.metadata?.templateName && (
                              <Chip
                                label={`Template: ${msg.attributes.metadata.templateName}`}
                                size="small"
                                sx={{ 
                                  mt: 0.5,
                                  fontSize: '0.7rem',
                                  height: '18px'
                                }}
                              />
                            )}
                            
                            {/* Image (backend URL or WhatsApp media ID via proxy) */}
                            {msg.attributes?.messageType === 'image' && (() => {
                              const mediaUrl = msg.attributes?.mediaUrl;
                              const { url: directUrl, mediaId: proxyMediaId } = getMessageImageDisplay(mediaUrl);
                              if (!mediaUrl) {
                                return (
                                  <Box sx={{ mt: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', p: 1, bgcolor: '#f0f2f5', borderRadius: '8px' }}>
                                      <ImageIcon sx={{ fontSize: 20, mr: 1, color: '#667781' }} />
                                      <Typography variant="caption" sx={{ color: '#667781', fontSize: '0.75rem' }}>
                                        Image (media not available)
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              }
                              if (directUrl) {
                                return (
                                  <Box sx={{ mt: 1 }}>
                                    <Box
                                      component="img"
                                      src={directUrl}
                                      alt="WhatsApp Image"
                                      sx={{
                                        maxWidth: '100%',
                                        maxHeight: '300px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        '&:hover': { opacity: 0.9 }
                                      }}
                                      onClick={() => window.open(directUrl, '_blank')}
                                    />
                                  </Box>
                                );
                              }
                              return (
                                <Box sx={{ mt: 1 }}>
                                  <WhatsAppMediaImage mediaId={proxyMediaId!} alt="WhatsApp Image" />
                                </Box>
                              );
                            })()}
                            {/* Voice message – WhatsApp-style player */}
                            {msg.attributes?.messageType === 'audio' && (() => {
                              const mediaUrl = msg.attributes?.mediaUrl;
                              const { url: directUrl, mediaId: proxyMediaId } = getMessageImageDisplay(mediaUrl);
                              const durationSeconds = msg.attributes?.metadata?.originalMessage?.audio?.voice?.duration_seconds;
                              if (!mediaUrl) {
                                return (
                                  <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', p: 1, bgcolor: '#f0f2f5', borderRadius: '8px' }}>
                                    <VoiceIcon sx={{ fontSize: 20, mr: 1, color: '#667781' }} />
                                    <Typography variant="caption" sx={{ color: '#667781' }}>Voice message (unavailable)</Typography>
                                  </Box>
                                );
                              }
                              if (directUrl) {
                                return (
                                  <Box sx={{ mt: 0.5 }}>
                                    <Box component="audio" src={directUrl} controls sx={{ maxWidth: '100%', height: 36 }} />
                                  </Box>
                                );
                              }
                              if (proxyMediaId) {
                                return (
                                  <Box sx={{ mt: 0.5 }}>
                                    <WhatsAppVoiceMessage
                                      mediaId={proxyMediaId}
                                      durationSeconds={durationSeconds}
                                      isOutbound={isOutbound}
                                    />
                                  </Box>
                                );
                              }
                              return null;
                            })()}
                            
                            {/* Video message */}
                            {msg.attributes?.messageType === 'video' && (() => {
                              const mediaUrl = msg.attributes?.mediaUrl;
                              const { url: directUrl, mediaId: proxyMediaId } = getMessageImageDisplay(mediaUrl);
                              const caption = msg.attributes?.message;
                              const hasCaption = caption && caption.trim() && caption !== 'Video';
                              if (!mediaUrl) {
                                return (
                                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', p: 1, bgcolor: '#f0f2f5', borderRadius: '8px' }}>
                                    <VideoIcon sx={{ fontSize: 24, mr: 1, color: '#667781' }} />
                                    <Typography variant="caption" sx={{ color: '#667781' }}>Video (unavailable)</Typography>
                                  </Box>
                                );
                              }
                              if (directUrl) {
                                return (
                                  <Box sx={{ mt: 1 }}>
                                    <Box component="video" src={directUrl} controls playsInline sx={{ maxWidth: '100%', maxHeight: 320, borderRadius: '8px', bgcolor: '#000' }} />
                                    {hasCaption && <Typography variant="body2" sx={{ mt: 0.5, color: '#111b21' }}>{caption}</Typography>}
                                  </Box>
                                );
                              }
                              return (
                                <Box sx={{ mt: 1 }}>
                                  <WhatsAppVideoMessage mediaId={proxyMediaId!} />
                                  {hasCaption && <Typography variant="body2" sx={{ mt: 0.5, color: '#111b21' }}>{caption}</Typography>}
                                </Box>
                              );
                            })()}
                            
                            {/* Document / file message */}
                            {msg.attributes?.messageType === 'document' && (() => {
                              const mediaUrl = msg.attributes?.mediaUrl;
                              const { url: directUrl, mediaId: proxyMediaId } = getMessageImageDisplay(mediaUrl);
                              const filename = msg.attributes?.message
                                || msg.attributes?.metadata?.originalMessage?.document?.filename
                                || 'Document';
                              if (!mediaUrl) {
                                return (
                                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', p: 1, bgcolor: '#f0f2f5', borderRadius: '8px' }}>
                                    <DocumentIcon sx={{ fontSize: 24, mr: 1, color: '#667781' }} />
                                    <Typography variant="caption" sx={{ color: '#667781' }}>Document (unavailable)</Typography>
                                  </Box>
                                );
                              }
                              if (directUrl) {
                                return (
                                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.08)', maxWidth: 280 }}>
                                  <DocumentIcon sx={{ fontSize: 36, color: '#667781' }} />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{filename}</Typography>
                                    <Button size="small" startIcon={<DownloadIcon />} href={directUrl} target="_blank" rel="noopener" sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.75rem' }}>Open / Download</Button>
                                  </Box>
                                </Box>
                                );
                              }
                              if (proxyMediaId) {
                                return (
                                  <Box sx={{ mt: 1 }}>
                                    <WhatsAppDocumentMessage mediaId={proxyMediaId} filename={filename} />
                                  </Box>
                                );
                              }
                              return null;
                            })()}
                            
                            {/* Location – Google Map link + embed */}
                            {msg.attributes?.messageType === 'location' && (() => {
                              const loc = msg.attributes?.metadata?.originalMessage?.location;
                              const lat = loc?.latitude != null ? Number(loc.latitude) : null;
                              const lng = loc?.longitude != null ? Number(loc.longitude) : null;
                              const fallbackMatch = (msg.attributes?.message || msg.message || '').match(/Lat\s*([\d.-]+).*Lon\s*([\d.-]+)/i);
                              const latitude = lat ?? (fallbackMatch ? parseFloat(fallbackMatch[1]) : null);
                              const longitude = lng ?? (fallbackMatch ? parseFloat(fallbackMatch[2]) : null);
                              if (latitude == null || longitude == null) {
                                return (
                                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', p: 1, bgcolor: '#f0f2f5', borderRadius: '8px' }}>
                                    <LocationOnIcon sx={{ fontSize: 24, mr: 1, color: '#667781' }} />
                                    <Typography variant="caption" sx={{ color: '#667781' }}>Location (unavailable)</Typography>
                                  </Box>
                                );
                              }
                              const mapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
                              const embedUrl = `https://www.google.com/maps?q=${latitude},${longitude}&output=embed`;
                              return (
                                <Box sx={{ mt: 1 }}>
                                  <Box
                                    sx={{
                                      borderRadius: '8px',
                                      overflow: 'hidden',
                                      border: '1px solid rgba(0,0,0,0.1)',
                                      maxWidth: 320,
                                    }}
                                  >
                                    <Box
                                      component="iframe"
                                      src={embedUrl}
                                      title="Location on Google Maps"
                                      sx={{
                                        width: '100%',
                                        height: 200,
                                        border: 0,
                                        display: 'block',
                                      }}
                                    />
                                    <Button
                                      fullWidth
                                      size="small"
                                      startIcon={<LocationOnIcon />}
                                      href={mapsUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        borderRadius: 0,
                                        textTransform: 'none',
                                        justifyContent: 'center',
                                        bgcolor: '#f5f5f5',
                                        '&:hover': { bgcolor: '#eee' },
                                      }}
                                    >
                                      Open in Google Maps
                                    </Button>
                                  </Box>
                                </Box>
                              );
                            })()}
                            
                            {/* Voice Response Playback */}
                            {hasVoiceResponse && (
                              <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (playingAudio === msg.id) {
                                      stopAudio();
                                    } else {
                                      playAudio(msg.attributes.metadata.aiResponse.audioResponse.audio_base64, msg.id);
                                    }
                                  }}
                                  sx={{ 
                                    width: 24, 
                                    height: 24,
                                    color: '#25d366'
                                  }}
                                >
                                  {playingAudio === msg.id ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                                </IconButton>
                                <Typography variant="caption" sx={{ color: '#667781', fontSize: '0.75rem', ml: 0.5 }}>
                                  Voice Response
                                </Typography>
                              </Box>
                            )}
                            
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'flex-end', 
                              alignItems: 'center',
                              mt: 0.5,
                              gap: 0.5
                            }}>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: '#667781',
                                  fontSize: '0.7rem',
                                  opacity: 0.8
                                }}
                              >
                                {formatMessageTime(msgTime)}
                              </Typography>
                              {isOutbound && (
                                <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                                  {status === 'read' ? (
                                    <CheckIcon sx={{ fontSize: 16, color: '#53bdeb' }} />
                                  ) : status === 'delivered' ? (
                                    <CheckIcon sx={{ fontSize: 16, color: '#667781' }} />
                                  ) : status === 'sent' ? (
                                    <CheckIcon sx={{ fontSize: 16, color: '#667781', opacity: 0.5 }} />
                                  ) : null}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </React.Fragment>
                      );
                    })}
                    {/* Scroll anchor */}
                    <div ref={messagesEndRef} />
                  </Stack>
                )}
              </Box>

              {/* Message Input - WhatsApp Style */}
              <Box sx={{ 
                p: 1.5, 
                bgcolor: '#f0f2f5',
                borderTop: '1px solid #e0e0e0'
              }}>
                {error && (
                  <Alert 
                    severity="error" 
                    onClose={() => setError('')}
                    sx={{ 
                      mb: 1.5,
                      '& .MuiAlert-message': { fontSize: '0.875rem' }
                    }}
                  >
                    {error}
                  </Alert>
                )}
                
                {success && (
                  <Alert 
                    severity="success" 
                    onClose={() => setSuccess('')}
                    sx={{ 
                      mb: 1.5,
                      '& .MuiAlert-message': { fontSize: '0.875rem' }
                    }}
                  >
                    {success}
                  </Alert>
                )}
                
                {isRecording && (
                  <Alert 
                    severity="warning"
                    sx={{ 
                      mb: 1.5,
                      '& .MuiAlert-message': { fontSize: '0.875rem' }
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <CircularProgress size={16} color="error" />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        Recording... {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        startIcon={<StopIcon />}
                        onClick={stopRecording}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                      >
                        Stop
                      </Button>
                    </Stack>
                  </Alert>
                )}
                
                <Stack direction="row" spacing={1} alignItems="flex-end">
                  <Tooltip title="Attach file">
                    <IconButton 
                      sx={{ 
                        color: '#54656f',
                        '&:hover': { bgcolor: '#e9edef' }
                      }}
                      disabled={sending || isRecording}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <AttachFileIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <TextField
                      inputRef={inputRef}
                      fullWidth
                      size="small"
                      placeholder={isRecording ? "Recording voice message..." : "Type a message (use '/' for quick messages)"}
                      value={newMessage}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      multiline
                      maxRows={4}
                      disabled={sending || isRecording}
                      sx={{
                        bgcolor: 'white',
                        borderRadius: '21px',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '21px',
                          '& fieldset': {
                            borderColor: '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#d0d0d0',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#25d366',
                          },
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '0.95rem',
                          py: 1.25,
                          px: 2,
                        }
                      }}
                    />
                    
                    {/* Autocomplete Dropdown */}
                    {autocompleteAnchor && autocompleteSuggestions.length > 0 && (
                      <ClickAwayListener onClickAway={() => {
                        setAutocompleteAnchor(null);
                        setAutocompleteQuery('');
                        setAutocompleteSuggestions([]);
                      }}>
                        <Paper
                          sx={{
                            position: 'absolute',
                            bottom: '100%',
                            left: 0,
                            right: 0,
                            mb: 1,
                            maxHeight: 300,
                            overflow: 'auto',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            zIndex: 1300,
                            borderRadius: '8px'
                          }}
                        >
                          <List dense sx={{ p: 0 }}>
                            {autocompleteSuggestions.map((msg, index) => (
                              <ListItemButton
                                key={msg.id}
                                selected={index === selectedSuggestionIndex}
                                onClick={() => {
                                  setNewMessage(msg.message);
                                  setAutocompleteAnchor(null);
                                  setAutocompleteQuery('');
                                  setAutocompleteSuggestions([]);
                                }}
                                sx={{
                                  '&.Mui-selected': {
                                    bgcolor: '#e3f2fd',
                                    '&:hover': {
                                      bgcolor: '#bbdefb',
                                    }
                                  }
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {msg.label}
                                      </Typography>
                                      <Chip 
                                        label={`/${autocompleteQuery || '...'}`}
                                        size="small"
                                        sx={{ 
                                          height: 18,
                                          fontSize: '0.65rem',
                                          bgcolor: '#f5f5f5'
                                        }}
                                      />
                                    </Box>
                                  }
                                  secondary={
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        color: 'text.secondary',
                                        fontSize: '0.7rem',
                                        mt: 0.5
                                      }}
                                    >
                                      {msg.message.substring(0, 60)}...
                                    </Typography>
                                  }
                                />
                              </ListItemButton>
                            ))}
                          </List>
                        </Paper>
                      </ClickAwayListener>
                    )}
                  </Box>
                  
                  
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault();
                      handleSendMessage();
                    }}
                    disabled={sending || isRecording || !newMessage.trim()}
                    sx={{
                      bgcolor: sending || isRecording || !newMessage.trim() ? '#e9edef' : '#25d366',
                      color: sending || isRecording || !newMessage.trim() ? '#aebac1' : 'white',
                      '&:hover': {
                        bgcolor: sending || isRecording || !newMessage.trim() ? '#e9edef' : '#128c7e',
                      },
                      width: 42,
                      height: 42,
                    }}
                  >
                    {sending ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SendIcon />
                    )}
                  </IconButton>
                </Stack>
                
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept="image/*,application/pdf,.doc,.docx"
                  onChange={(e) => {
                    // Handle file upload
                    const file = e.target.files?.[0];
                    if (file) {
                      console.log('File selected:', file.name);
                      // TODO: Implement file upload
                    }
                  }}
                />
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                flexDirection: 'column'
              }}
            >
              <Box sx={{ 
                width: 200, 
                height: 200, 
                borderRadius: '50%', 
                bgcolor: '#e9edef',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3
              }}>
                <WhatsAppIcon sx={{ fontSize: 100, color: '#667781' }} />
              </Box>
              <Typography variant="h6" sx={{ color: '#41525d', mb: 1 }}>
                Select a contact to start chatting
              </Typography>
              <Typography variant="body2" sx={{ color: '#667781' }}>
                Your messages will appear here
              </Typography>
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default WhatsAppChat;

